import React, { useEffect, useState, useSyncExternalStore } from 'react';
import { Box, Button, Typography } from '@mui/material';
import {
  getDrafts,
  subscribeDrafts,
  serialiseDrafts,
  parseHostConfig,
  replaceDrafts,
  deviationCount,
} from './copilotProfileDraft';

/**
 * The host config, in full, as the source it will be pasted into.
 *
 * The fragment switches decide one graphic at a time; this is the whole set at
 * once — what to paste into copilotChromeProfiles.js, and the way back in when
 * the map came from another browser or machine. It is a dock panel rather than
 * a field in the strip because the thing being read is a block of source, and
 * a one-line box can neither show it nor be trusted to hold it.
 *
 * Editable, so a line can be deleted or a graphic renamed in place. Applying
 * reads the text rather than running it: the content arrives by paste, so
 * `eval` would run whatever was on the clipboard.
 */
const PANEL_SX = {
  width: 460,
  bgcolor: '#141414',
  border: '1px solid rgba(226,134,27,0.45)',
  borderRadius: '4px',
  display: 'flex',
  flexDirection: 'column',
  gap: 1,
  px: 1.5,
  py: 1.25,
};

const BUTTON_SX = {
  minWidth: 0,
  px: 1.25,
  py: 0.25,
  fontSize: '0.72rem',
  color: '#E2861B',
  border: '1px solid rgba(226,134,27,0.45)',
  '&:hover': { borderColor: '#E2861B', bgcolor: 'rgba(226,134,27,0.08)' },
  '&.Mui-disabled': { color: 'rgba(255,255,255,0.25)', borderColor: 'rgba(255,255,255,0.12)' },
};

export default function HostConfigView() {
  const drafts = useSyncExternalStore(subscribeDrafts, getDrafts);
  const committed = serialiseDrafts(drafts);

  /*
   * Held as a draft so the text can be edited without every keystroke
   * rewriting the map. It follows the store whenever that changes underneath —
   * a switch flipped in the strip shows up here immediately.
   */
  const [text, setText] = useState(committed);
  const [status, setStatus] = useState('');
  useEffect(() => { setText(committed); setStatus(''); }, [committed]);

  const dirty = text !== committed;

  const flash = (value) => { setStatus(value); setTimeout(() => setStatus(''), 1600); };

  const apply = () => {
    const parsed = parseHostConfig(text);
    if (!parsed) { flash('Not a config'); return; }
    replaceDrafts(parsed);
    flash('Applied');
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      flash('Copied');
    } catch {
      // Clipboard refused; the text is on screen and selectable anyway.
      flash('Select and copy');
    }
  };

  return (
    <Box sx={PANEL_SX} role="group" aria-label="Host config">
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography
          sx={{
            fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.12em',
            textTransform: 'uppercase', color: '#E2861B',
          }}
        >
          Host config
        </Typography>
        <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.35)' }}>
          {deviationCount(drafts)} graphic{deviationCount(drafts) === 1 ? '' : 's'}
        </Typography>
        {status && (
          <Typography
            role="status"
            sx={{
              fontSize: '0.72rem',
              color: status === 'Not a config' ? '#D1030D' : '#3AA564',
            }}
          >
            {status}
          </Typography>
        )}
      </Box>

      <Box
        component="textarea"
        aria-label="Host config source"
        spellCheck={false}
        value={text}
        onChange={(e) => setText(e.target.value)}
        // The strip listens for Escape to close itself; typing here must not.
        onKeyDown={(e) => e.stopPropagation()}
        sx={{
          width: '100%',
          minHeight: 150,
          resize: 'vertical',
          boxSizing: 'border-box',
          p: 1,
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
          fontSize: '0.72rem',
          lineHeight: 1.5,
          color: '#fff',
          bgcolor: '#0d0d0d',
          border: '1px solid rgba(255,255,255,0.16)',
          borderRadius: '3px',
          '&:focus': { outline: 'none', borderColor: '#E2861B' },
        }}
      />

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Button size="small" onClick={copy} sx={BUTTON_SX}>Copy</Button>
        <Button size="small" onClick={apply} disabled={!dirty} sx={BUTTON_SX}>Apply</Button>
        <Typography sx={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.3)', ml: 'auto' }}>
          {dirty ? 'Edited — Apply to load' : 'Paste over this to load a config'}
        </Typography>
      </Box>
    </Box>
  );
}
